import { Box, Typography } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import AddIcon from '@mui/icons-material/Add'
import AdxStatusBar from 'components/reusable/statusBar/AdxStatusBar'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  getAllEvaluation,
  deleteEvaluation,
  createEvaluation,
  duplicateEvaluation,
} from 'services/api/modules/performance/appraisal/evaluation' // eslint-disable-line react-hooks/exhaustive-deps
import AdxPopper from 'components/reusable/AdxPopper'
import LoadingSkeleton from './LoadingSkeleton'
import { getAllDepartment } from 'services/api/modules/performance/appraisal/department'
import AdxButton from 'components/custom/AdxButton'

const EvaluationList = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const [evaluationList, setEvaluationList] = useState([])

  useEffect(() => {
    queryClient.resetQueries('allEvaluationData')
    queryClient.resetQueries('evaluatiallDepartmentDataListonData')
    queryClient.invalidateQueries('allEvaluationData')
    queryClient.invalidateQueries('allDepartmentDataList')
  }, [])

  const {
    isLoading: createLoading,
    error: createError,
    mutate: createMutate,
  } = useMutation((values) => createEvaluation(values), {
    onSuccess: async () => {
      queryClient.invalidateQueries('allEvaluationData')
      queryClient.invalidateQueries('allDepartmentDataList')
    },
  })

  const {
    isLoading: duplicateLoading,
    error: duplicateError,
    mutate: duplicateMutate,
  } = useMutation((evalId) => duplicateEvaluation(evalId), {
    onSuccess: async () => {
      queryClient.resetQueries('allEvaluationData')
      queryClient.resetQueries('evaluatiallDepartmentDataListonData')
      queryClient.invalidateQueries('allEvaluationData')
      queryClient.invalidateQueries('allDepartmentDataList')
    },
  })

  const {
    isLoading: deleteLoading,
    error: deleteError,
    mutate: deleteMutate,
  } = useMutation((id) => deleteEvaluation(id), {
    onSuccess: async () => {
      queryClient.resetQueries('allEvaluationData')
      queryClient.resetQueries('evaluatiallDepartmentDataListonData')
      queryClient.invalidateQueries('allEvaluationData')
      queryClient.invalidateQueries('allDepartmentDataList')
    },
    onError: async () => {
      queryClient.invalidateQueries('allEvaluationData')
    },
  })

  const {
    isLoading: evaluationLoading,
    error: evaluationError,
    data,
  } = useQuery(
    'allEvaluationData',
    async () => {
      const { data } = await getAllEvaluation()
      return data
    },
    {
      onSuccess: () => queryClient.invalidateQueries('allDepartmentDataList'),
    }
  )

  const { data: allDepartmentData } = useQuery(
    'allDepartmentDataList',
    async () => {
      let newData = [{ name: 'All Departments', id: 'all' }]
      const { data } = await getAllDepartment()
      data.map((item, index) => newData.push(data[index]))
      return newData
    },
    {
      onSuccess: () => {
        setEvaluationList(updatedEvaluationList())
      },
    }
  )

  useEffect(() => {
    setEvaluationList(data ?? [])
  }, [data])

  const updatedEvaluationList = () => {
    return evaluationList?.map((evalList) => {
      const participantDeptNames = mapDepartmentIdsToNames(evalList?.participant_departments)
      let list = participantDeptNames

      typeof participantDeptNames === 'object'
        ? participantDeptNames.length < 4
          ? (list = participantDeptNames.map((item) => item).join(', '))
          : (list = `${participantDeptNames.length} Departments`)
        : (list = participantDeptNames)

      return {
        ...evalList,
        participant_departments: list,
      }
    })
  }

  const mapDepartmentIdsToNames = (departmentIds) => {
    if (typeof departmentIds === 'object') {
      return departmentIds?.map((departmentId) => {
        const department = allDepartmentData?.find((d) => d.id === departmentId)
        return department ? department.name : null
      })
    } else return departmentIds
  }

  const duplicateHandler = (evalId) => {
    duplicateMutate(evalId)
  }

  const editHandler = (evalId) => {
    navigate(`${location.pathname}/evaluation/edit/${evalId}`)
  }

  const deleteHandler = (evalId) => {
    deleteMutate(evalId)
  }

  const viewHandler = (evalId) => {
    navigate(`${location.pathname}/evaluation/preview/${evalId}`)
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box>
        <Typography variant="h4" color="dark-text.main" fontWeight="bold">
          Evaluation
        </Typography>
        <Typography variant="body1" color="light-gray-text.main">
          Create or use ready made evaluation template
        </Typography>
      </Box>
      <Box
        sx={{
          bgcolor: 'white.main',
          border: 1,
          borderColor: 'gray-line.main',
          borderRadius: '4.5px',
        }}
      >
        {evaluationLoading ? (
          <LoadingSkeleton />
        ) : (
          evaluationList?.map((i, index) => (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              py={1}
              px={3}
              sx={{ borderBottom: 1, borderColor: 'gray-line.main' }}
              key={index}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  flexDirection: 'column',
                }}
              >
                <Box display="flex" gap={1} alignItems="center">
                  <Typography variant="body1" color="dark-text.main">
                    {i.title}
                  </Typography>
                  {i.is_template === 2 ? (
                    <AdxStatusBar
                      status={{
                        text: 'Template',
                        color: 'alternate.main',
                        bgColor: 'alternate.light',
                      }}
                    />
                  ) : null}
                </Box>
                <Box display="flex" gap={1} alignItems="center">
                  <Typography variant={'subtitle2'} sx={{ color: 'light-gray-text.main' }}>
                    {i.participant_match_type_name.charAt(0).toUpperCase() +
                      i.participant_match_type_name.slice(1)}
                  </Typography>
                  <Typography variant={'subtitle2'} sx={{ color: 'light-gray-text.main' }}>
                    <li>{i.participant_departments}</li>
                  </Typography>
                </Box>
              </Box>
              <Box display="flex" alignItems="center" gap={2.5}>
                <Typography
                  variant="subtitle1"
                  color="alternate.main"
                  onClick={() => viewHandler(i.id)}
                  disabled={deleteLoading || evaluationLoading ? true : false}
                  sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                >
                  Preview
                </Typography>
                <AdxPopper buttonIcon="more_horiz" placement="bottom-start" sx={{ marginTop: 1 }}>
                  <Typography
                    variant="subtitle1"
                    onClick={() => duplicateHandler(i.id)}
                    disabled={deleteLoading || evaluationLoading ? true : false}
                    sx={{ width: '100px' }}
                  >
                    Duplicate
                  </Typography>
                  {i.is_template !== 2 ? (
                    <Typography
                      variant="subtitle1"
                      color="alternate.main"
                      onClick={() => editHandler(i.id)}
                      disabled={deleteLoading || evaluationLoading ? true : false}
                    >
                      Edit
                    </Typography>
                  ) : null}
                  {i.is_template !== 2 ? (
                    <Typography
                      variant="subtitle1"
                      color="error.main"
                      onClick={() => deleteHandler(i.id)}
                      disabled={deleteLoading || evaluationLoading ? true : false}
                    >
                      {deleteLoading ? 'Deleting...' : 'Delete'}
                    </Typography>
                  ) : null}
                </AdxPopper>
              </Box>
            </Box>
          ))
        )}

        {createLoading ? (
          <Box display="flex" alignItems="center" justifyContent="space-between" py={2} px={3}>
            <Typography variant="body1" color="dark-text.main">
              Creating new evaluation...
            </Typography>
          </Box>
        ) : null}
        <Box sx={{ p: 0.5 }}>
          <AdxButton
            variant={'ghost'}
            fullWidth
            startIcon={<AddIcon color="alternate" />}
            onClick={() => {
              navigate(`${location.pathname}/evaluation/create`)
            }}
          >
            Add custom evaluation
          </AdxButton>
        </Box>
      </Box>
    </Box>
  )
}

export default EvaluationList
